import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {EcommerceService} from "../../ecommerce.service";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {IEcommerceProduct} from "../../ecommerce-product.interface";
import {debounceTime, filter, first, takeUntil, tap} from "rxjs/operators";
import {IEcommerceEntityState} from "../../../../../store/reducers/ecommerce.reducer";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {Subject} from "rxjs";
import {RemoveItemDialogComponent} from "../remove-item-dialog/remove-item-dialog.component";
import {Config} from "../../../../../config/config";
import {EcommerceAnalyticsLabel} from "../../ecommerce-product.enum";
import {DbModalService} from "../../../../../modules/modal/modal";
import {TextUtils} from "../../../../../shared/utils/text-utils";

interface IProductsParams {
  websiteId: number;
  order?: 'asc' | 'desc';
  status?: string;
  search?: string;
  page?: number;
  per_page?: number;
  stock_status?: string;
}

@Component({
    selector: 'db-products-list',
    templateUrl: './products-list.component.html',
    styleUrls: ['./products-list.component.scss'],
    standalone: false
})
export class ProductsListComponent implements OnInit, OnDestroy {
  tabs = [
    {
      value: 'any',
      label: TextUtils.getText('text_allProducts')
    },
    {
      value: 'publish',
      label: TextUtils.getText('text_active')
    },
    {
      value: 'draft',
      label: TextUtils.getText('text_draft')
    },
    {
      value: 'instock',
      label: TextUtils.getText('text_inStock')
    },
    {
      value: 'outofstock',
      label: TextUtils.getText('text_outOfStock')
    }
  ];
  activeTab = this._activatedRoute.snapshot.queryParams['status'] || 'any';
  isMobile = false;
  searchTerm: FormControl = new FormControl();
  website = this._activatedRoute.snapshot.parent.parent.parent.data['website'];
  order: 'asc' | 'desc' = 'desc';
  products: IEcommerceProduct[] = [];
  currentPage = 1;
  loading = true;
  showSearchLoading = false;
  productsCount = 0;
  productsPerPage = 50;
  isFakeMode = this.website.id === Config.demoDomainId;
  searched = false;
  _activeStockStatus = null;
  _stockStatuses = ['instock', 'outofstock'];
  websiteUrl = this.website.site_url;
  currencySymbol = '$';
  private _subscription$: Subject<void> = new Subject<void>();
  private _analyticsActionName = 'Ecommerce - Products';

  constructor(private _activatedRoute: ActivatedRoute,
              private _store: Store<fromStore.DbStoreState>,
              private _modalService: DbModalService,
              private _ecommerceService: EcommerceService) {
  }

  ngOnInit() {
    this._dispatchProducts();
    this._getProducts();
    this._listenSearch();
    this._listenFailure();
    this._dispatchDropdowns();
  }

  private _dispatchDropdowns() {
    this._store.dispatch(new fromStore.LoadEcommerceProductDropdowns({websiteId: this.website.id}));
    this._store.select(fromStore.getDropdowns(this.website.id))
      .pipe(
        takeUntil(this._subscription$),
        filter(res => !!res)
      ).subscribe((response) => {
      this.currencySymbol = response.currency_symbol;
    });
  }

  private _listenFailure() {
    // this._store.select(fromStore.getProductsLoading(this.website.id))
    //   .pipe(takeUntil(this._subscription$))
    //   .subscribe((loading: boolean) => this.loading = loading);
  }

  private _getProducts() {
    this._store.select(fromStore.getProducts(this.website.id))
      .pipe(
        filter((prodState: IEcommerceEntityState) => prodState && prodState.loaded),
        takeUntil(this._subscription$)
      ).subscribe((response) => {
      this.loading = false;
      this.products = response.products;
      this.productsCount = response.productsCount;
      this.searched = !!this.searchTerm.value;
      this.showSearchLoading = false;
    });
  }

  private _listenSearch() {
    this.searchTerm.valueChanges.pipe(
      tap(() => this.showSearchLoading = true),
      debounceTime(500),
      takeUntil(this._subscription$)
    ).subscribe((searchValue) => {
      this.currentPage = 1;
      this._dispatchProducts(false);
    });
  }

  setActiveTab(id: string) {
    if (this._stockStatuses.includes(id)) {
      this._activeStockStatus = id;
      id = 'any';
    } else {
      this._activeStockStatus = null;
    }
    this.activeTab = id;
    this.currentPage = 1;
    this._dispatchProducts();
  }

  onRemoveProduct(id: number) {
    this._modalService
      .open(RemoveItemDialogComponent)
      .closed.subscribe(value => {
      if (value) {
        this._removeProduct(id);
      }
    });
  }

  private _removeProduct(id: number) {
    this.loading = true;
    this._ecommerceService.deleteProduct(this.website.id, id)
      .subscribe((value) => {
        if (!value) {
          return;
        }
        if (value.success) {
          this._dispatchProducts();
          NotifierComponent.notify(new Notification(TextUtils.getText('text_productDeleted'), NotificationType.SUCCESS));
          this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.DELETE);
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrong'), NotificationType.DANGER));
          this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.DELETE, true);
        }
      });
  }

  public duplicateProduct(id: number) {
    this.loading = true;
    this._ecommerceService.duplicateProduct(this.website.id, id)
      .subscribe((value) => {
        if (!value) {
          return;
        }
        if (value.success) {
          this._dispatchProducts();
          NotifierComponent.notify(new Notification(TextUtils.getText('text_productDuplicated'), NotificationType.SUCCESS));
        } else {
          this.loading = false;
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrong'), NotificationType.DANGER));
        }
      });
  }

  changeStatus(value: { productId: number, status: string }) {
    const {productId, status} = value;
    this._ecommerceService.updateProduct(this.website.id, productId, {status})
      .pipe(
        filter(response => !!response),
        first()
      ).subscribe((response) => {
      if (response && response.success) {
        if (this.activeTab !== 'any') {
          this.products = this.products.filter((prod) => {
            if (prod.id === value.productId) {
              return this.activeTab === value.status;
            }
            return true;
          });
        }
        const prod = this.products.find(prod => prod.id === value.productId);
        prod.slug = response.data.slug;
        document.getElementById('products-wrapper').click();
        NotifierComponent.notify(new Notification(TextUtils.getText('text_statusUpdated'), NotificationType.SUCCESS));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CHANGE_STATUS);
      } else {
        NotifierComponent.notify(new Notification("Something went wrong", NotificationType.DANGER));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CHANGE_STATUS, true);
      }
    });
  }

  private _dispatchProducts(showLoading = true) {
    const params: IProductsParams = {
      websiteId: this.website.id,
      order: this.order,
      status: this.activeTab,
      page: this.currentPage,

      per_page: this.productsPerPage
    };

    if (this.searchTerm.value) {
      params.search = this.searchTerm.value;
    }

    if (this._activeStockStatus) {
      params.stock_status = this._activeStockStatus;
    }

    this.loading = showLoading;

    this._store.dispatch(new fromStore.LoadEcommerceProducts(params));
  }

  reorderProducts() {
    if (!this.products.length) {
      return;
    }
    this.order = this.order === 'asc' ? 'desc' : 'asc';
    this.currentPage = 1;
    this._dispatchProducts();
  }

  changePage(page: number) {
    this.currentPage = page;
    this._dispatchProducts();
  }

  ngOnDestroy() {
    this._subscription$.next();
  }
}
